import { XStack, YStack } from '@lyra/core/components'
import BodyText from '@lyra/core/components/BodyText'
import Button from '@lyra/core/components/Button'
import Icon, { PlusSquare } from '@lyra/core/components/Icon'
import NumberInput from '@lyra/core/components/Input/NumberInput'
import Link from '@lyra/core/components/Link'
import Section from '@lyra/core/components/Section'
import VideoText from '@lyra/core/components/VideoText'
import filterNulls from '@lyra/core/utils/filterNulls'
import formatNumber from '@lyra/core/utils/formatNumber'
import { SelectedInstrument } from '@lyra/web/constants/instruments'
import { Position } from '@lyra/web/constants/position'
import {
  DEFAULT_RFQ_RATIO,
  DEFAULT_RFQ_SIZE,
  SendRfqParams,
  SendRfqResponse,
} from '@lyra/web/constants/rfqs'
import useDebounce from '@lyra/web/hooks/useDebounce'
import useRfqBestQuote from '@lyra/web/hooks/useRfqBestQuote'
import useSubaccount from '@lyra/web/hooks/useSubaccount'
import useTickers from '@lyra/web/hooks/useTickers'
import { getRfqGcdAmount, getRfqMaxTotalCost } from '@lyra/web/utils/rfqs'
import { useCallback, useMemo, useState } from 'react'

import TradeErrorNotice from '../TradeErrorNotice'
import TradeExecuteButtonRow from '../TradeExecuteButtonRow'
import RfqSelectedInstrumentRow from './RfqSelectedInstrumentRow'
import RfqTradeFormReceipt from './RfqTradeFormReceipt'

type Props = {
  sendRfqParams: SendRfqParams
  onChangeRfqParams: (
    sendRfqParams: SendRfqParams | ((old: SendRfqParams) => SendRfqParams | null)
  ) => void
  onSendRfq: (rfq: SendRfqResponse) => void
  onRemoveInstrument?: (option: SelectedInstrument) => void
  hideReceipt?: boolean
  showAddLegsPrompt?: boolean
}

const RfqSendTradeForm = ({
  sendRfqParams,
  onChangeRfqParams,
  onSendRfq,
  onRemoveInstrument,
  hideReceipt,
  showAddLegsPrompt,
}: Props) => {
  const { sendRfq, subaccount } = useSubaccount()

  const tickers = useTickers(sendRfqParams.legs.map((leg) => leg.instrument_name))

  const [sendRfqError, setSendRfqError] = useState<Error>()

  const {
    data: bestQuoteEstimate,
    error: quoteError,
    isLoading,
  } = useRfqBestQuote(useDebounce(sendRfqParams, 100))

  const error = sendRfqError ?? quoteError

  const _isCustomRatio = useMemo(() => {
    const firstLeg = sendRfqParams.legs[0]
    if (!firstLeg) {
      return false
    }
    const isSameAmount = sendRfqParams.legs.every((leg) => leg.amount === firstLeg.amount)
    return !isSameAmount
  }, [sendRfqParams])

  const [isDefaultCustomRatio, setIsDefaultCustomRatio] = useState(_isCustomRatio)

  const isCustomRatio = _isCustomRatio
    ? true
    : sendRfqParams.legs.length > 1
    ? isDefaultCustomRatio
    : false

  const amount = useMemo(() => {
    const amount = getRfqGcdAmount(sendRfqParams.legs)
    return amount ? amount : DEFAULT_RFQ_SIZE
  }, [sendRfqParams.legs])

  const legRatios = useMemo(() => {
    return sendRfqParams.legs.reduce(
      (dict, leg) => ({
        ...dict,
        [leg.instrument_name]: amount > 0 ? +leg.amount / amount : 0,
      }),
      {} as Record<string, number>
    )
  }, [sendRfqParams, amount])

  const handleChangeLegRatio = useCallback(
    (instrumentName: string, ratio: number) => {
      onChangeRfqParams((sendRfqParams) => {
        const legIndex = sendRfqParams.legs.findIndex(
          (leg) => leg.instrument_name === instrumentName
        )
        if (legIndex === -1) {
          console.warn('invalid instrumentName in handleChangeLegRatio')
          return sendRfqParams
        }

        const baseAmount = getRfqGcdAmount(sendRfqParams.legs)
        const newLegs = [...sendRfqParams.legs]
        newLegs[legIndex].amount = (baseAmount * ratio).toString()

        return {
          ...sendRfqParams,
          legs: newLegs,
        }
      })
    },
    [onChangeRfqParams]
  )

  const handleChangeAmount = useCallback(
    (_newAmount: number) => {
      // IMPORTANT!! do not let amount be set to 0, always default to 1
      const newAmount = _newAmount ? _newAmount : DEFAULT_RFQ_SIZE

      onChangeRfqParams((sendRfqParams) => {
        const currAmount = getRfqGcdAmount(sendRfqParams.legs)

        const newLegs = sendRfqParams.legs.map((leg) => {
          const currLegRatio = currAmount > 0 ? +leg.amount / currAmount : DEFAULT_RFQ_RATIO
          const newLegAmount = currLegRatio * newAmount

          return {
            ...leg,
            amount: newLegAmount.toString(),
          }
        })

        return {
          ...sendRfqParams,
          legs: newLegs,
        }
      })
    },
    [onChangeRfqParams]
  )

  const handleChangeCustomRatio = useCallback(
    (isCustomRatio: boolean) => {
      if (isCustomRatio) {
        setIsDefaultCustomRatio(true)
      } else {
        onChangeRfqParams((sendRfqParams) => {
          const currAmount = getRfqGcdAmount(sendRfqParams.legs)

          // reset ratios to amount
          const newLegs = sendRfqParams.legs.map((leg) => ({
            ...leg,
            amount: currAmount.toString(),
          }))

          return {
            ...sendRfqParams,
            legs: newLegs,
          }
        })
        setIsDefaultCustomRatio(false)
      }
    },
    [onChangeRfqParams]
  )

  const handleSendRfq = useCallback(async () => {
    setSendRfqError(undefined)

    try {
      if (!bestQuoteEstimate) {
        throw new Error('No best quote')
      }

      if (!subaccount) {
        throw new Error('No subaccount')
      }

      if (!tickers) {
        throw new Error('Tickers not loaded')
      }

      const indexPrice = +Object.values(tickers)[0].index_price

      const sendRfqResult = await sendRfq({
        ...sendRfqParams,
        max_total_cost: getRfqMaxTotalCost(sendRfqParams, bestQuoteEstimate, indexPrice).toString(),
        subaccount_id: subaccount.subaccount_id,
      })

      onSendRfq(sendRfqResult)
    } catch (error) {
      if (error instanceof Error) {
        setSendRfqError(error)
      }
      console.error(error)
    }
  }, [bestQuoteEstimate, subaccount, tickers, sendRfq, sendRfqParams, onSendRfq])

  const openPositions = useMemo(() => {
    return sendRfqParams.legs.reduce(
      (dict, leg) => {
        const openPosition = subaccount?.positions
          .filter((p) => !!+p.amount)
          .find((pos) => pos.instrument_name === leg.instrument_name)
        if (!openPosition) {
          return dict
        }
        return {
          ...dict,
          [leg.instrument_name]: openPosition,
        }
      },
      {} as Record<string, Position>
    )
  }, [subaccount, sendRfqParams])

  const isClosingPositions = useMemo(
    () =>
      Object.values(openPositions).length
        ? sendRfqParams.legs.every((leg) => {
            const position = openPositions[leg.instrument_name]
            if (!position) {
              return false
            }
            const amountDir = leg.direction === 'buy' ? +leg.amount : -+leg.amount
            return +position.amount + amountDir === 0
          })
        : false,
    [sendRfqParams, openPositions]
  )

  const isClosingPositionsDict: Record<string, boolean> = useMemo(() => {
    return Object.values(openPositions).length
      ? sendRfqParams.legs.reduce(
          (dict, leg) => {
            const position = openPositions[leg.instrument_name]
            if (!position) {
              return dict
            }
            const amountDir = leg.direction === 'buy' ? +leg.amount : -+leg.amount
            return {
              ...dict,
              [position.instrument_name]: +position.amount + amountDir === 0,
            }
          },
          {} as Record<string, boolean>
        )
      : {}
  }, [sendRfqParams, openPositions])

  const handleClosePositions = useCallback(() => {
    if (!Object.values(openPositions).length || isClosingPositions) {
      return
    }
    onChangeRfqParams((params) => ({
      ...params,
      legs: filterNulls(Object.values(openPositions)).map((pos) => ({
        instrument_name: pos.instrument_name,
        amount: Math.abs(+pos.amount).toString(),
        direction: +pos.amount >= 0 ? 'sell' : 'buy',
      })),
    }))
  }, [openPositions, isClosingPositions, onChangeRfqParams])

  const hasMultiOpenPositions = Object.values(openPositions).length > 1
  const isMultiLeg = sendRfqParams.legs.length > 1

  return (
    <>
      {sendRfqParams.legs.map((leg) => (
        <RfqSelectedInstrumentRow
          key={leg.instrument_name}
          instrumentName={leg.instrument_name}
          isBuy={leg.direction === 'buy'}
          isEditingRatio={isCustomRatio}
          ratio={legRatios[leg.instrument_name]}
          position={openPositions[leg.instrument_name]}
          isClosing={isClosingPositionsDict[leg.instrument_name]}
          showRatio={isCustomRatio}
          onChangeRatio={(ratio) => handleChangeLegRatio(leg.instrument_name, ratio)}
          onDeselect={onRemoveInstrument ? () => onRemoveInstrument(leg) : undefined}
        />
      ))}
      {sendRfqParams.legs.length <= 1 && showAddLegsPrompt ? (
        <Section.XStack>
          <XStack gap="$1" alignItems="center">
            <YStack>
              <BodyText>Add Options</BodyText>
              <XStack gap="$0.5" alignItems="center">
                <Icon icon={<PlusSquare strokeWidth={1.5} />} color="secondary" size={17} />
                <BodyText color="secondary">Select from board</BodyText>
              </XStack>
            </YStack>
          </XStack>
        </Section.XStack>
      ) : null}
      {isMultiLeg ? (
        <Section.XStack isCompact>
          <BodyText color="secondary">
            <Link
              label={isCustomRatio ? 'Use 1:1' : 'Use Ratio'}
              onPress={() => handleChangeCustomRatio(!isCustomRatio)}
            />
            {hasMultiOpenPositions ? (
              <>
                &nbsp;·&nbsp;
                <Link
                  label={'Close All'}
                  isActive={isClosingPositions}
                  onPress={handleClosePositions}
                />
              </>
            ) : null}
          </BodyText>
        </Section.XStack>
      ) : null}
      <Section.XStack>
        <NumberInput
          onChangeValue={handleChangeAmount}
          value={amount}
          defaultValue={DEFAULT_RFQ_SIZE}
          width="100%"
          textAlign="right"
          leftContent={<VideoText color="secondary">Amount</VideoText>}
          formatValue={(val) => formatNumber(val, { minDps: 1 })}
        />
      </Section.XStack>
      <TradeExecuteButtonRow>
        {error ? <TradeErrorNotice error={error} /> : null}
        <Button
          width="100%"
          label="Request Quote"
          size="lg"
          color="cta"
          isOutlined
          onPress={handleSendRfq}
        />
      </TradeExecuteButtonRow>
      {!hideReceipt ? (
        <>
          <Section.Separator />
          <RfqTradeFormReceipt
            rfq={sendRfqParams}
            tickers={tickers}
            quote={bestQuoteEstimate}
            isLoading={isLoading}
          />
        </>
      ) : null}
    </>
  )
}

export default RfqSendTradeForm
